import { motion } from "framer-motion";
import { Lightbulb, Brain, Cpu, Smartphone, Eye, Activity, Leaf, Network, Users, Handshake, Building } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import { useTranslation } from "@/hooks/useTranslation";
const BuildingInnovation = () => {
  const {
    t
  } = useTranslation();
  const focusAreas = [{
    icon: Brain,
    solidColor: "#A9E0DD"
  }, {
    icon: Activity,
    solidColor: "#66B3BB"
  }, {
    icon: Smartphone,
    solidColor: "#C8DDFF"
  }, {
    icon: Cpu,
    solidColor: "#868EAD"
  }, {
    icon: Eye,
    solidColor: "#B4C3FD"
  }, {
    icon: Activity,
    solidColor: "#70989c"
  }, {
    icon: Leaf,
    solidColor: "#A9E0DD"
  }, {
    icon: Network,
    solidColor: "#66B3BB"
  }];
  return <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      {/* Flowing background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
      <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />
      <div className="absolute bottom-0 right-1/4 w-1/2 h-[40%] bg-gradient-to-t from-secondary/10 via-transparent to-transparent blur-3xl" />
      
      <Navigation />
      
      {/* Hero Section with Geometric Background */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        
        <div className="container-padding relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div className="inline-flex items-center gap-2 bg-gradient-to-r from-secondary/10 to-accent-teal/10 border border-secondary/20 rounded-full px-6 py-3 text-secondary font-medium backdrop-blur-sm mb-8" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }}>
              <Lightbulb className="w-4 h-4" />
              {t.buildingInnovation.badge}
            </motion.div>
            
            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text mb-8 font-bold text-accent-teal break-words hyphens-auto px-4">
              {t.buildingInnovation.title}
            </motion.h1>
            
            <motion.p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed px-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.4
          }}>
              {t.buildingInnovation.subtitle}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      

      {/* Areas of Focus */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50/30 relative overflow-hidden bg-sky-100">
        <div className="container-padding">
          <div className="max-w-7xl mx-auto">
            <motion.div className="text-center mb-20" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }}>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6 break-words hyphens-auto px-4">{t.buildingInnovation.focusAreasTitle}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t.buildingInnovation.focusAreasSubtitle}
              </p>
            </motion.div>

            {/* Flowing Timeline Design */}
            <div className="relative">
              {/* Central flowing path */}
              <div className="absolute inset-0 flex justify-center pointer-events-none">
                <svg width="100%" height="100%" className="absolute inset-0" viewBox="0 0 800 1400" preserveAspectRatio="xMidYMid meet">
                  <path d="M400 80 Q200 180 400 280 Q600 380 400 480 Q200 580 400 680 Q600 780 400 880 Q200 980 400 1080 Q600 1180 400 1280" stroke="url(#flowGradient)" strokeWidth="3" fill="none" strokeDasharray="15,10" className="animate-pulse" />
                  <defs>
                    <linearGradient id="flowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#A9E0DD" stopOpacity="0.9" />
                      <stop offset="30%" stopColor="#66B3BB" stopOpacity="0.7" />
                      <stop offset="70%" stopColor="#C8DDFF" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#868EAD" stopOpacity="0.4" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              {/* Focus areas arranged along the path */}
              <div className="relative space-y-4">
                {focusAreas.map((area, index) => {
                const isEven = index % 2 === 0;
                return <motion.div key={t.buildingInnovation.focusAreas[index]} className={`flex items-center gap-4 sm:gap-8 ${isEven ? 'flex-row' : 'flex-row-reverse'} relative`} initial={{
                  opacity: 0,
                  x: isEven ? -100 : 100,
                  y: 50
                }} whileInView={{
                  opacity: 1,
                  x: 0,
                  y: 0
                }} transition={{
                  duration: 0.8,
                  delay: index * 0.15,
                  type: "spring",
                  stiffness: 80
                }} viewport={{
                  once: true
                }}>
                      {/* Connection node */}
                      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                        
                      </div>

                      {/* Content card */}
                      <motion.div className={`flex-1 max-w-md ${isEven ? 'mr-auto' : 'ml-auto'} group cursor-pointer`} whileHover={{
                    scale: 1.05,
                    y: -8,
                    transition: {
                      duration: 0.4,
                      type: "spring",
                      stiffness: 300
                    }
                  }}>
                        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl backdrop-blur-md border border-white/20 shadow-2xl hover:shadow-4xl transition-all duration-700 bg-white/10 hover:bg-white/15">
                          {/* Gradient overlay */}
                          <div className="absolute inset-0 opacity-20" style={{
                        background: `linear-gradient(135deg, ${area.solidColor}40, transparent 70%)`
                      }} />
                          
                          {/* Glowing border effect */}
                          <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                        boxShadow: `0 0 40px ${area.solidColor}30`
                      }} />

                          <div className="relative p-4 sm:p-6 md:p-8">
                            {/* Icon with enhanced styling */}
                            <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative overflow-hidden flex-shrink-0" style={{
                          background: `linear-gradient(135deg, ${area.solidColor}, ${area.solidColor}CC)`
                        }}>
                              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl" />
                              <area.icon className="w-7 h-7 sm:w-10 sm:h-10 text-white relative z-10" />
                            </div>

                             {/* Enhanced typography */}
                            <div className="space-y-3 sm:space-y-4 min-w-0">
                              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold transition-colors leading-tight text-slate-800 break-words hyphens-auto" style={{ wordBreak: 'break-word' }}>
                                {t.buildingInnovation.focusAreas[index]}
                              </h3>
                              
                              {/* Animated progress bar */}
                              <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-700 group-hover:w-full w-1/3" style={{
                              backgroundColor: area.solidColor
                            }} />
                              </div>

                              {/* Floating particles effect */}
                              <div className="absolute top-4 right-4 w-2 h-2 rounded-full opacity-60 animate-pulse" style={{
                            backgroundColor: area.solidColor
                          }} />
                              <div className="absolute top-8 right-8 w-1 h-1 rounded-full opacity-40 animate-pulse" style={{
                            backgroundColor: area.solidColor,
                            animationDelay: '1s'
                          }} />
                              <div className="absolute bottom-8 left-8 w-1.5 h-1.5 rounded-full opacity-50 animate-pulse" style={{
                            backgroundColor: area.solidColor,
                            animationDelay: '2s'
                          }} />
                            </div>
                          </div>

                          {/* Bottom accent */}
                          <div className="absolute bottom-0 left-0 right-0 h-1" style={{
                        background: `linear-gradient(90deg, transparent, ${area.solidColor}, transparent)`
                      }} />
                        </div>
                      </motion.div>
                    </motion.div>;
              })}
              </div>

              {/* Floating background elements */}
              <div className="absolute top-10 left-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl animate-pulse" />
              <div className="absolute bottom-10 right-10 w-40 h-40 bg-secondary/5 rounded-full blur-2xl animate-pulse" style={{
              animationDelay: '1s'
            }} />
              <div className="absolute top-1/2 right-20 w-24 h-24 bg-accent-teal/5 rounded-full blur-xl animate-pulse" style={{
              animationDelay: '2s'
            }} />
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="py-24 bg-white">
        <div className="container-padding">
          <motion.div className="max-w-6xl mx-auto" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }}>
            <div className="text-center mb-16 relative z-10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-6 break-words hyphens-auto px-4">
                {t.buildingInnovation.partnerTitle}
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {t.buildingInnovation.partnerSubtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[{
              icon: Users,
              key: 'academic' as const,
              solidColor: "#A9E0DD"
            }, {
              icon: Building,
              key: 'healthcare' as const,
              solidColor: "#66B3BB"
            }, {
              icon: Handshake,
              key: 'industry' as const,
              solidColor: "#C8DDFF"
            }].map((partner, index) => <motion.div key={partner.key} className="group cursor-pointer" initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: index * 0.2
            }} viewport={{
              once: true
            }} whileHover={{
              scale: 1.05,
              y: -8
            }}>
                  <div className="relative overflow-hidden rounded-2xl border border-white/20 backdrop-blur-sm transition-all duration-300 h-full flex flex-col" style={{
                backgroundColor: partner.solidColor + '15'
              }}>
                    <div className="absolute inset-0 opacity-10" style={{
                  background: `linear-gradient(135deg, ${partner.solidColor}40, ${partner.solidColor}20)`
                }} />
                    
                    <div className="relative p-8 text-center">
                      <div className="w-16 h-16 rounded-xl mb-6 flex items-center justify-center shadow-lg mx-auto transform rotate-3 group-hover:rotate-0 transition-transform duration-300" style={{
                    backgroundColor: partner.solidColor + '20'
                  }}>
                        <partner.icon className="w-8 h-8" style={{
                      color: partner.solidColor
                    }} />
                      </div>
                      
                      <h3 className="font-bold mb-4 text-slate-800 py-0 px-0 mx-0 text-xl">
                        {t.buildingInnovation.partners[partner.key].title}
                      </h3>
                      
                      <p className="text-slate-700 mb-6 leading-relaxed">
                        {t.buildingInnovation.partners[partner.key].description}
                      </p>
                      
                      
                    </div>
                    
                    <div className="absolute bottom-0 left-0 right-0 h-1" style={{
                  backgroundColor: partner.solidColor
                }} />
                  </div>
                </motion.div>)}
            </div>

            
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default BuildingInnovation;